import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../components/SafeIcon';
import { FiActivity, FiMapPin, FiClock, FiChevronRight, FiTerminal } from 'react-icons/fi';
import { neighborhoods, liveBoards } from '../data/mockData';

const Dashboard = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-20 md:pb-0">
      
      {/* Neighborhood Guide Scanner */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-mono text-transit-neon flex items-center">
            <SafeIcon icon={FiActivity} className="mr-2" />
            ZONE_ANALYSIS
          </h2>
          <button className="text-xs font-mono text-gray-400 hover:text-amber-500 flex items-center transition-colors">
            EXPAND_MAP <SafeIcon icon={FiChevronRight} />
          </button>
        </div>

        <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory hide-scrollbar">
          {neighborhoods.map((hood, idx) => (
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              key={hood.id} 
              className="min-w-[280px] sm:min-w-[320px] bg-asphalt-800 border border-asphalt-600 rounded-lg overflow-hidden snap-center group relative"
            >
              {/* Overlay styling */}
              <div className="absolute inset-0 bg-transit-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 pointer-events-none" />
              
              <div className="h-32 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-asphalt-800 to-transparent z-10" />
                <img 
                  src={hood.image} 
                  alt={hood.name} 
                  className="w-full h-full object-cover filter grayscale group-hover:grayscale-0 contrast-125 transition-all duration-500"
                />
                <div className="absolute top-2 right-2 z-20 bg-asphalt-900/80 backdrop-blur px-2 py-1 rounded text-[10px] font-mono text-amber-500 border border-amber-500/30 flex items-center">
                  ACT_SCORE: {hood.score}
                </div>
              </div>
              
              <div className="p-4 relative z-20">
                <h3 className="text-lg font-bold text-white mb-1 group-hover:text-transit-neon transition-colors">{hood.name}</h3>
                <p className="text-xs font-mono text-gray-400 mb-3 flex items-center">
                  <SafeIcon icon={FiMapPin} className="mr-1" /> {hood.coords}
                </p>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {hood.tags.map(tag => (
                    <span key={tag} className="text-[10px] font-mono px-2 py-1 rounded bg-asphalt-700 text-gray-300 border border-asphalt-600">
                      {tag}
                    </span>
                  ))}
                </div>
                
                <div className="flex justify-between items-center pt-3 border-t border-asphalt-700">
                  <span className="text-xs font-mono text-amber-500 flex items-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-2 animate-pulse" />
                    {hood.status}
                  </span>
                  <button className="text-xs font-mono bg-transit-500/20 text-transit-neon px-3 py-1 rounded border border-transit-500/30 hover:bg-transit-500 hover:text-white transition-colors">
                    SCAN
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Live Local Boards */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-mono text-transit-neon flex items-center border-b border-asphalt-700 pb-2">
            <SafeIcon icon={FiTerminal} className="mr-2" />
            LIVE_DATA_STREAM
          </h2>
          
          <div className="bg-asphalt-800 border border-asphalt-600 rounded-lg p-1">
            {liveBoards.map((board, idx) => (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + (idx * 0.1) }}
                key={board.id} 
                className="flex items-start p-3 border-b border-asphalt-700/50 last:border-0 hover:bg-asphalt-700/50 transition-colors rounded"
              >
                <div className={`mt-1 mr-3 w-2 h-2 rounded-full ${
                  board.type === 'alert' ? 'bg-red-500 shadow-[0_0_8px_#ef4444]' : 
                  board.type === 'discovery' ? 'bg-amber-500 shadow-[0_0_8px_#FFB300]' : 
                  'bg-transit-neon shadow-[0_0_8px_#00E5FF]'
                }`} />
                <div className="flex-1">
                  <p className="text-sm">
                    <span className="font-mono text-transit-400">@{board.user}</span>{' '}
                    <span className="text-gray-400">{board.action}</span>{' '}
                    <span className="text-white font-medium">{board.location}</span>
                  </p>
                  <div className="flex items-center mt-1 text-[10px] font-mono text-gray-500">
                    <SafeIcon icon={FiClock} className="mr-1" />
                    {board.time}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Quick Actions / Status */}
        <div className="space-y-4">
          <h2 className="text-lg font-mono text-transit-neon flex items-center border-b border-asphalt-700 pb-2">
            SYS_STATUS
          </h2>
          <div className="bg-asphalt-800 border border-asphalt-600 rounded-lg p-4 font-mono text-sm space-y-3">
             <div className="flex justify-between items-center">
               <span className="text-gray-400">NETWORK</span>
               <span className="text-transit-neon">OPTIMAL</span>
             </div>
             <div className="flex justify-between items-center">
               <span className="text-gray-400">NODES_SCANNED</span>
               <span className="text-white">1,402</span>
             </div>
             <div className="flex justify-between items-center">
               <span className="text-gray-400">SYNERGY</span>
               <span className="text-amber-500">89%</span>
             </div>
             <div className="pt-3 border-t border-asphalt-700">
               <button className="w-full py-2 bg-transit-600 hover:bg-transit-500 text-white rounded transition-colors flex items-center justify-center">
                 INITIATE DEEP SCAN
               </button>
             </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Dashboard;